import { getLogger } from '@ravago/shared/logging/client';
import { QueryBuilder } from '@ravago/shared/next-data-access';
import { LogMessageUtils } from '@ravago/shared/page-data/utils/logging';

import { Asset, CreateClientParams, Entry, createClient as createPublicClient } from 'contentful';
import { PlainClientAPI, createClient as createManagementClient } from 'contentful-management';
import { ZodSafeParseResult } from 'zod';

import { ContentfulCollectionName, ContentfulEnvironment } from '../../models/contentful';
import {
  TypeBlogPost,
  TypeBrand,
  TypeCaseStudy,
  TypeDynamicPageFields,
  TypeGlobalSection,
  TypeGlobalSectionFields,
  TypeIndustry,
  TypeLandingPageFields,
  TypeProcess,
  TypeProducer,
  TypeProduct,
  TypeSavedSectionFields,
  TypeSectionTemplateFields,
  TypeSegment
} from '../../models/contentful-generated';
import {
  NormalizedAsset,
  NormalizedCollection,
  NormalizedDynamicPage,
  NormalizedEntity,
  NormalizedGlobalSection,
  NormalizedPage,
  NormalizedSavedSection,
  NormalizedSectionTemplate
} from '../../models/contentful-mapped';
import { validateAndFilterBlogPostEntries, validateBlogPostEntry } from '../../validators/blog-post.validator';
import { validateAndFilterBrandEntries, validateBrandEntry } from '../../validators/brand.validator';
import { validateAndFilterCaseStudyEntries, validateCaseStudyEntry } from '../../validators/case-study.validator';
import { validateAssetEntries } from '../../validators/contentful-asset.validator';
import { validateAndFilterDynamicPagePickerEntries } from '../../validators/dynamic-page.validator';
import { validateGlobalSectionEntries } from '../../validators/global-section.validator';
import { validateAndFilterIndustryEntries, validateIndustryEntry } from '../../validators/industry.validator';
import { validateAndFilterLandingPagePickerEntries } from '../../validators/landing-page.validator';
import { validateAndFilterProcessEntries, validateProcessEntry } from '../../validators/process.validator';
import { validateAndFilterProducerEntries, validateProducerEntry } from '../../validators/producer.validator';
import { validateAndFilterProductEntries, validateProductEntry } from '../../validators/product.validator';
import {
  validateAndFilterLocalizedSalesTermsAndConditionsEntries,
  validateLocalizedSalesTermsAndConditionsEntry
} from '../../validators/sales-terms-and-conditions.validator';
import { validateAndFilterSavedSectionEntries } from '../../validators/saved-section.validator';
import { validateAndFilterSectionTemplateEntries } from '../../validators/section-template.validator';
import { validateAndFilterSegmentEntries, validateSegmentEntry } from '../../validators/segment.validator';
import { DataMapperUtils } from '../data-mapper';

export class ContentfulUtils {
  static contentfulCache: Map<string, NormalizedCollection<NormalizedEntity>> = new Map();

  static getDefaultCollection<T = unknown>(limit = 0, skip = 0, total = 0, items: T[] = []): NormalizedCollection<T> {
    return {
      items,
      total,
      limit,
      skip
    };
  }

  /**
   * Get all entries from a collection. (paginated)
   */
  static async getAllFromCollection(
    env: ContentfulEnvironment,
    contentType: ContentfulCollectionName,
    page: number,
    perPage: number,
    search?: string,
    ids?: Array<string>,
    locale?: string
  ): Promise<NormalizedCollection<NormalizedEntity>> {
    const client = createPublicClient(env);

    const skip = (page > 0 ? page - 1 : page) * perPage;

    if (Array.isArray(ids) && !ids.length) return ContentfulUtils.getDefaultCollection();

    const queryOptions = new QueryBuilder()
      .contentType(contentType)
      .limit(perPage)
      .skip(skip)
      .search(search)
      .locale(locale ?? 'en')
      .setArrayOptional('sys.id[in]', ids)
      .include(10);

    try {
      const entries = await client.getEntries(queryOptions.build());

      if (!entries.total) return ContentfulUtils.getDefaultCollection();

      const mappedEntries = this.normalizeResponse(entries.items, contentType) as NormalizedEntity[];

      if (!mappedEntries.length) return ContentfulUtils.getDefaultCollection();

      return ContentfulUtils.getDefaultCollection(perPage, skip, entries.total, mappedEntries);
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  }

  /**
   * Get entry by id.
   * Using client to include all fields of the requested entry.
   */
  static async getEntry(env: ContentfulEnvironment, entryId: string, contentType: ContentfulCollectionName): Promise<NormalizedEntity | null> {
    const client = createPublicClient(env);

    try {
      /**
       * Entries for salesTermsAndConditions should contain all locales
       * as it is legally required to have them all on the same page.
       */
      const entry = await client.getEntry(entryId, { include: 10, ...(contentType === 'salesTermsAndConditions' && { locale: '*' }) });

      return this.normalizeResponse(entry, contentType) as NormalizedEntity;
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return null;
    }
  }

  /**
   * Get all file like entries. (paginated)
   */
  static async getAssetCollection(
    env: ContentfulEnvironment,
    contentTypes: string[],
    page: number,
    perPage: number,
    search?: string
  ): Promise<NormalizedCollection<NormalizedAsset>> {
    const client = createPublicClient(env);

    const skip = page * perPage;

    const queryOptions = new QueryBuilder()
      .limit(perPage)
      .skip(skip)
      .search(search)
      .setArrayOptional('fields.file.contentType[in]', contentTypes)
      .include(4);

    try {
      const entries = await client.getAssets(queryOptions.build());

      if (!entries.total) return ContentfulUtils.getDefaultCollection();

      const validationResult = validateAssetEntries(entries.items as unknown as Asset[]);

      if (!validationResult.success) {
        console.error(LogMessageUtils.validationFailed('ASSETS'), validationResult.error.message);

        return ContentfulUtils.getDefaultCollection();
      }

      entries.items = validationResult.data as any;

      return DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedAsset>>(entries, DataMapperUtils.normalizeContentfulAsset);
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  }

  /**
   * Get landing page entries with server-side pagination and optional search/filter.
   * Only fetches the fields required for the page picker UI to minimise payload.
   */
  static async getPages(
    env: ContentfulEnvironment,
    locale: string,
    preview = false,
    perPage = 20,
    page = 0,
    search?: string,
    draftOnly = false
  ): Promise<NormalizedCollection<NormalizedPage>> {
    const clientParams: CreateClientParams = {
      space: env.space,
      environment: env.environment,
      accessToken: preview && env.previewToken ? env.previewToken : env.accessToken,
      host: preview ? 'preview.contentful.com' : 'cdn.contentful.com'
    };

    const client = createPublicClient(clientParams);

    const queryBuilder = new QueryBuilder()
      .contentType('landingPage')
      .limit(perPage)
      .skip(page * perPage)
      .locale(locale)
      .select('sys,fields.internalName,fields.route,fields.accessType,fields.theme')
      .order('fields.internalName')
      .include(0);

    if (search) {
      queryBuilder.set('fields.internalName[match]', search);
    }

    if (draftOnly) {
      queryBuilder.set('sys.publishedAt[exists]', 'false');
    }

    try {
      const entries = await client.getEntries<TypeLandingPageFields>(queryBuilder.build());

      const validEntries = validateAndFilterLandingPagePickerEntries(entries.items);

      if (!validEntries.length) {
        return ContentfulUtils.getDefaultCollection(perPage, page * perPage, entries.total);
      }

      entries.items = validEntries;

      return DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedPage>>(
        entries,
        DataMapperUtils.normalizeContentfulLandingPage
      );
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  }

  /**
   * Get dynamic page entries with server-side pagination and optional search/filter.
   * Only fetches the fields required for the page picker UI to minimise payload.
   */
  static async getDynamicPages(
    env: ContentfulEnvironment,
    locale: string,
    preview = false,
    perPage = 20,
    page = 0,
    search?: string,
    draftOnly = false
  ): Promise<NormalizedCollection<NormalizedDynamicPage>> {
    const clientParams: CreateClientParams = {
      space: env.space,
      environment: env.environment,
      accessToken: preview && env.previewToken ? env.previewToken : env.accessToken,
      host: preview ? 'preview.contentful.com' : 'cdn.contentful.com'
    };

    const client = createPublicClient(clientParams);

    const queryBuilder = new QueryBuilder()
      .contentType('dynamicPage')
      .limit(perPage)
      .skip(page * perPage)
      .locale(locale)
      .select('sys,fields.internalName,fields.route,fields.accessType,fields.theme,fields.contentType')
      .order('fields.internalName')
      .include(0);

    if (search) {
      queryBuilder.set('fields.internalName[match]', search);
    }

    if (draftOnly) {
      queryBuilder.set('sys.publishedAt[exists]', 'false');
    }

    try {
      const entries = await client.getEntries<TypeDynamicPageFields>(queryBuilder.build());

      // Use the picker-specific validator that only checks the fields present in our select.
      const validEntries = validateAndFilterDynamicPagePickerEntries(entries.items);

      if (!validEntries.length) {
        return ContentfulUtils.getDefaultCollection(perPage, page * perPage, entries.total);
      }

      entries.items = validEntries;

      return DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedDynamicPage>>(
        entries,
        DataMapperUtils.normalizeContentfulDynamicPage
      );
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  }

  /**
   * Fetch a single page's summary (type, route, contentType field) using minimal data transfer.
   * Used to restore selection state on initial render without loading the full page tree.
   */
  static async getPageSummary(
    env: ContentfulEnvironment,
    locale: string,
    pageId: string,
    preview = false
  ) {
    const clientParams: CreateClientParams = {
      space: env.space,
      environment: env.environment,
      accessToken: preview && env.previewToken ? env.previewToken : env.accessToken,
      host: preview ? 'preview.contentful.com' : 'cdn.contentful.com'
    };

    const client = createPublicClient(clientParams);

    try {
      const entry = await client.getEntry<{route?: string, contentType?: string}>(pageId, {
        locale,
        select: 'sys,fields.route,fields.contentType',
        include: 0
      });

      const pageType = entry.sys.contentType.sys.id as 'landingPage' | 'dynamicPage';

      if (pageType !== 'landingPage' && pageType !== 'dynamicPage') {
        return null;
      }

      return {
        id: entry.sys.id,
        pageType,
        route: entry.fields.route,
        contentType: entry.fields.contentType
      };
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return null;
    }
  }

  /**
   * Fetch the full pageContent tree for a single page entry.
   * Only called on-demand for the currently selected page (to extract anchors).
   */
  static async getPageContentById(
    env: ContentfulEnvironment,
    locale: string,
    pageId: string,
    preview = false
  ) {
    const clientParams: CreateClientParams = {
      space: env.space,
      environment: env.environment,
      accessToken: preview && env.previewToken ? env.previewToken : env.accessToken,
      host: preview ? 'preview.contentful.com' : 'cdn.contentful.com'
    };

    const client = createPublicClient(clientParams);

    try {
      const entry = await client.getEntry<{pageContent: object[]}>(pageId, { locale, include: 10 });

      return entry.fields.pageContent ?? [];
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return [];
    }
  }

  /**
   * Get all global section entries.
   */
  static async getGlobalSections(env: ContentfulEnvironment): Promise<NormalizedCollection<NormalizedGlobalSection>> {
    const client = createPublicClient(env);

    const queryOptions = new QueryBuilder().contentType('globalSection').limit(1000).include(10);

    try {
      const entries = await client.getEntries<TypeGlobalSectionFields>(queryOptions.build());

      const validationResult = validateGlobalSectionEntries(entries.items);

      if (!validationResult.success) {
        console.error(LogMessageUtils.validationFailed('GLOBAL_SECTIONS'), validationResult.error.message);

        return ContentfulUtils.getDefaultCollection();
      }

      entries.items = validationResult.data as unknown as TypeGlobalSection[];

      return DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedGlobalSection>>(
        entries,
        DataMapperUtils.normalizeContentfulGlobalSection
      );
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  }

  /**
   * Get all saved section entries.
   */
  static async getSavedSections(env: ContentfulEnvironment): Promise<NormalizedCollection<NormalizedSavedSection>> {
    const client = createPublicClient(env);

    const queryOptions = new QueryBuilder().contentType('savedSection').limit(1000).include(10);

    try {
      const entries = await client.getEntries<TypeSavedSectionFields>(queryOptions.build());

      const validEntries = validateAndFilterSavedSectionEntries(entries.items);

      entries.items = validEntries;

      return DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedSavedSection>>(
        entries,
        DataMapperUtils.normalizeContentfulSavedSection
      );
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  }

  /**
   * Save a section.
   */
  static async saveSavedSection(
    env: ContentfulEnvironment,
    body: Pick<NormalizedSavedSection, 'label' | 'category' | 'pageContent'>
  ): Promise<boolean> {
    const { space, environment, managementToken } = env;

    if (!managementToken) return false;

    const client: PlainClientAPI = createManagementClient(
      { accessToken: managementToken },
      { type: 'plain', defaults: { spaceId: space, environmentId: environment } }
    );

    const { label, category, pageContent } = body;

    try {
      // Create entry first.
      const newEntry = await client.entry.create(
        { contentTypeId: 'savedSection' },
        { fields: { label: { en: label }, category: { en: category }, node: { en: pageContent?.[0] } } }
      );

      // Publish created entry.
      await client.entry.publish({ entryId: newEntry.sys.id }, { ...newEntry });

      return true;
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return false;
    }
  }

  /**
   * Delete a saved section.
   */
  static async deleteSavedSection(env: ContentfulEnvironment, id: string): Promise<boolean> {
    const { space, environment, managementToken } = env;

    if (!managementToken) return false;

    const client: PlainClientAPI = createManagementClient(
      { accessToken: managementToken },
      { type: 'plain', defaults: { spaceId: space, environmentId: environment } }
    );

    try {
      // We'll do a soft delete for now.
      await client.entry.unpublish({ entryId: id });

      return true;
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return false;
    }
  }

  /**
   * Get all section template entries.
   */
  static async getSectionTemplates(env: ContentfulEnvironment): Promise<NormalizedCollection<NormalizedSectionTemplate>> {
    const clientParams: CreateClientParams = {
      space: env.mainSpace?.space ?? env.space,
      environment: env.environment,
      accessToken: env.mainSpace?.accessToken ?? env.accessToken,
      host: 'cdn.contentful.com'
    };

    const client = createPublicClient(clientParams);

    const queryOptions = new QueryBuilder().contentType('sectionTemplate').limit(1000).include(10);

    try {
      const entries = await client.getEntries<TypeSectionTemplateFields>(queryOptions.build());

      const validEntries = validateAndFilterSectionTemplateEntries(entries.items);

      entries.items = validEntries;

      return DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedSectionTemplate>>(
        entries,
        DataMapperUtils.normalizeContentfulSectionTemplate
      );
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  }

  static getDisplayFieldForContentType(contentType: ContentfulCollectionName): string {
    switch (contentType) {
      case 'caseStudy':
      case 'blogPost':
      case 'salesTermsAndConditions':
        return 'title';
      default:
        return 'name';
    }
  }

  static getImageFieldsForContentType(contentType: ContentfulCollectionName): string[] | undefined {
    switch (contentType) {
      case 'blogPost':
      case 'industry':
      case 'process':
        return ['image'];
      case 'product':
        return ['image', 'marketSegments[0].image'];
      case 'brand':
        return ['brandBannerImage'];
      case 'producer':
        return ['logo'];
      case 'caseStudy':
        return ['images[0]'];
      default:
        return undefined;
    }
  }

  /**
   * When entry is an array, the validated array will contain all valid entries. Returns an empty array when none are valid.
   *
   * @param entry
   * @param contentType
   */
  static normalizeResponse(
    entry: Entry<unknown> | Entry<unknown>[],
    contentType: ContentfulCollectionName
  ): NormalizedEntity | NormalizedEntity[] | null {
    let validationResult: ZodSafeParseResult<unknown>;

    switch (contentType) {
      case 'blogPost':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterBlogPostEntries(entry as TypeBlogPost[]),
            DataMapperUtils.normalizeContentfulBlogPost
          );
        }

        validationResult = validateBlogPostEntry(entry as TypeBlogPost);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('BLOG'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulBlogPost(validationResult.data as TypeBlogPost);
      case 'brand':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterBrandEntries(entry as TypeBrand[]),
            DataMapperUtils.normalizeContentfulBrand
          );
        }

        validationResult = validateBrandEntry(entry as TypeBrand);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('BRAND'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulBrand(validationResult.data as TypeBrand);
      case 'caseStudy':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterCaseStudyEntries(entry as TypeCaseStudy[]),
            DataMapperUtils.normalizeContentfulCaseStudy
          );
        }

        validationResult = validateCaseStudyEntry(entry as TypeCaseStudy);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('CASE_STUDY'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulCaseStudy(validationResult.data as TypeCaseStudy);
      case 'industry':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterIndustryEntries(entry as TypeIndustry[]),
            DataMapperUtils.normalizeContentfulMarket
          );
        }

        validationResult = validateIndustryEntry(entry as TypeIndustry);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('MARKET'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulMarket(validationResult.data as TypeIndustry);
      case 'producer':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterProducerEntries(entry as TypeProducer[]),
            DataMapperUtils.normalizeContentfulProducer
          );
        }

        validationResult = validateProducerEntry(entry as TypeProducer);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('PRODUCER'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulProducer(validationResult.data as TypeProducer);
      case 'product':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterProductEntries(entry as TypeProduct[]),
            DataMapperUtils.normalizeContentfulProduct
          );
        }

        validationResult = validateProductEntry(entry as TypeProduct);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('PRODUCT'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulProduct(validationResult.data as TypeProduct);
      case 'process':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterProcessEntries(entry as TypeProcess[]),
            DataMapperUtils.normalizeContentfulTechnology
          );
        }

        validationResult = validateProcessEntry(entry as TypeProcess);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('TECHNOLOGY'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulTechnology(validationResult.data as TypeProcess);
      case 'segment':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterSegmentEntries(entry as TypeSegment[]),
            DataMapperUtils.normalizeContentfulSegment
          );
        }

        validationResult = validateSegmentEntry(entry as TypeSegment);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('SEGMENT'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulSegment(validationResult.data as TypeSegment);
      case 'salesTermsAndConditions':
        if (Array.isArray(entry)) {
          return DataMapperUtils.normalizeContentfulCollection(
            validateAndFilterLocalizedSalesTermsAndConditionsEntries(entry),
            DataMapperUtils.normalizeContentfulLocalizedSalesTermCondition
          );
        }

        validationResult = validateLocalizedSalesTermsAndConditionsEntry(entry);

        if (!validationResult.success) {
          console.error(LogMessageUtils.validationFailed('SALES_TERMS_AND_CONDITIONS'), validationResult.error.message);

          return null;
        }

        return DataMapperUtils.normalizeContentfulLocalizedSalesTermCondition(validationResult.data);
      default:
        return Array.isArray(entry) ? [] : null;
    }
  }
}
